import { useSyncExternalStore } from 'react';
import type { VectorExportFeature } from '@geosoft/viewer-sdk';

export type VectorEditOperation = 'create' | 'update' | 'delete';

export interface VectorEditResult {
  readonly operation: VectorEditOperation;
  readonly feature?: VectorExportFeature;
  readonly featureId?: string | number;
}

export interface VectorEditStyle {
  readonly strokeColor: string;
  readonly strokeWidth: number;
  readonly fillColor: string;
  readonly fillOpacity: number;
}

export interface VectorSessionFeature extends VectorEditStyle {
  readonly layerId: string;
  readonly feature: VectorExportFeature;
  readonly operation: 'create' | 'update';
}

interface LayerEdits {
  readonly created: Map<string, VectorSessionFeature>;
  readonly updated: Map<string, VectorSessionFeature>;
  readonly deleted: Set<string>;
}

const editsByLayer = new Map<string, LayerEdits>();
const listeners = new Set<() => void>();
let version = 0;

function featureKey(feature: VectorExportFeature): string | null {
  const value =
    feature.id
    ?? feature.properties?.id
    ?? feature.properties?.OBJECTID
    ?? feature.properties?.OBJECTID_1
    ?? feature.properties?.objectid
    ?? feature.properties?.gid;
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
}

function layerEdits(layerId: string): LayerEdits {
  const existing = editsByLayer.get(layerId);
  if (existing) return existing;
  const created: LayerEdits = {
    created: new Map(),
    updated: new Map(),
    deleted: new Set(),
  };
  editsByLayer.set(layerId, created);
  return created;
}

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

export function commitVectorSessionEdit(
  layerId: string,
  result: VectorEditResult,
  style: VectorEditStyle,
): void {
  const state = layerEdits(layerId);
  if (result.operation === 'delete') {
    const key =
      result.featureId == null
        ? result.feature
          ? featureKey(result.feature)
          : null
        : String(result.featureId);
    if (!key) return;
    if (state.created.delete(key)) {
      state.updated.delete(key);
      state.deleted.delete(key);
    } else {
      state.updated.delete(key);
      state.deleted.add(key);
    }
    notify();
    return;
  }

  if (!result.feature) return;
  const key = featureKey(result.feature);
  if (!key) return;
  const entry: VectorSessionFeature = {
    layerId,
    feature: result.feature,
    operation: result.operation,
    ...style,
  };
  state.deleted.delete(key);
  if (result.operation === 'create' || state.created.has(key)) {
    state.created.set(key, { ...entry, operation: 'create' });
    state.updated.delete(key);
  } else {
    state.updated.set(key, entry);
  }
  notify();
}

export function mergeVectorSessionFeatures(
  layerId: string,
  source: ReadonlyArray<VectorExportFeature>,
): VectorExportFeature[] {
  const state = editsByLayer.get(layerId);
  if (!state) return [...source];
  const merged: VectorExportFeature[] = [];
  const seen = new Set<string>();
  for (const feature of source) {
    const key = featureKey(feature);
    if (!key) {
      merged.push(feature);
      continue;
    }
    seen.add(key);
    if (state.deleted.has(key)) continue;
    merged.push(state.updated.get(key)?.feature ?? state.created.get(key)?.feature ?? feature);
  }
  for (const [key, entry] of state.created) {
    if (!seen.has(key) && !state.deleted.has(key)) merged.push(entry.feature);
  }
  return merged;
}

export function getVectorSessionFeatures(): readonly VectorSessionFeature[] {
  const output: VectorSessionFeature[] = [];
  for (const state of editsByLayer.values()) {
    output.push(...state.created.values(), ...state.updated.values());
  }
  return output;
}

/**
 * Returns only the persistent session features (created + modified) for a
 * single layer. Adapters use this to re-hydrate their permanent overlay
 * layers on every tool switch so a Draw commit survives tool changes.
 */
export function getVectorSessionFeaturesForLayer(
  layerId: string,
): readonly VectorSessionFeature[] {
  const state = editsByLayer.get(layerId);
  if (!state) return [];
  return [...state.created.values(), ...state.updated.values()];
}

export function getDeletedVectorFeatureIds(layerId: string): readonly string[] {
  return [...(editsByLayer.get(layerId)?.deleted ?? [])];
}

export function useVectorEditSessionVersion(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => version,
    () => 0,
  );
}

export function clearVectorEditSessionForTests(): void {
  editsByLayer.clear();
  notify();
}
